import type { NonNegativeInteger } from '../types';

export const mergeAndDedupe = <T>(list1: T[], list2: T[], getter: (item: T) => unknown): T[] => {
  const merged = [...list1, ...list2];
  return merged.filter((item, index, self) => self.findIndex((t) => getter(t) === getter(item)) === index);
};

// Updates item in array while short circuiting after item is found
export const updateUniqueItem = <T extends { id: string }>(
  predicate: (item: T) => boolean,
  updatedItem: T,
  items: T[],
): T[] => {
  const index = items.findIndex(predicate);

  if (index === -1) {
    return items;
  }

  return [...items.slice(0, index), updatedItem, ...items.slice(index + 1)];
};
export const removeByIndex = <T>(list: T[], index: number): T[] => [...list.slice(0, index), ...list.slice(index + 1)];

export const shift = <T>(list: T[]): T[] => list.slice(1);

export const append = <T>(list: T[], ...items: T[]): T[] => [...list, ...items];

export const pop = <T>(list: T[]): T[] => list.slice(0, -1);

export const prepend = <T>(list: T[], ...items: T[]): T[] => [...items, ...list];

export const findFirstN = <T, N extends number>(
  list: T[],
  condition: (item: T) => boolean,
  n: NonNegativeInteger<N>,
): T[] => list.filter(condition).slice(0, n);

export const isLast = <T>(list: T[], index: number) => index === list.length - 1;

export const chunkParts = <T>(array: T[], chunkSize: number): T[][] => {
  // If chunkSize is invalid or array is not an array, return an empty array
  if (chunkSize <= 0 || !Array.isArray(array)) {
    return [];
  }

  // eslint-disable-next-line fp/no-let
  let remainingArray = array; // Holds the part of the array yet to be chunked

  return Array.from({ length: chunkSize }, (_, index) => {
    // Calculate the size of the current chunk
    const size = Math.ceil(remainingArray.length / (chunkSize - index));

    // Take the first 'size' elements as the current chunk
    const chunk = remainingArray.slice(0, size);

    // Update the remaining array by removing the elements of the current chunk
    // eslint-disable-next-line fp/no-mutation
    remainingArray = remainingArray.slice(size);

    // Return the current chunk
    return chunk;
  });
};

export const getPreviousItem = <TStep extends string, TSteps extends readonly TStep[]>(
  steps: TSteps,
  currentStep: TStep,
) => {
  const currentIndex = steps.indexOf(currentStep);
  // Can't decrement if already at the first step
  if (currentIndex === 0) return;
  return steps[currentIndex - 1]!;
};

export const getNextItem = <TStep extends string, TSteps extends readonly TStep[]>(
  steps: TSteps,
  currentStep: TStep,
) => {
  const currentIndex = steps.indexOf(currentStep);
  // Can't increment if already at the last step
  if (currentIndex === steps.length - 1) return;
  return steps[currentIndex + 1]!;
};

export const splitIntoGroups = <T>(array: T[], numberOfGroups: number) => {
  // Initialize an array with the specified number of empty groups
  const initialGroups: T[][] = Array.from({ length: numberOfGroups }, () => []);

  return array.reduce((acc, task, index) => {
    // Determine the group index for the current task
    const groupIndex = (index + 1) % numberOfGroups;
    // Push the task into the appropriate group
    // eslint-disable-next-line fp/no-mutating-methods
    acc[groupIndex]?.push(task);
    return acc;
  }, initialGroups);
};

export const removeNullish = <T>(array: (T | null | undefined)[]): T[] =>
  array.filter((item): item is T => item !== null && item !== undefined);
